import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  Menu,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { timeEntriesAPI } from '../services/api';
import { localISODate } from '../utils/dates';

/**
 * TaskTimerButton - Inline timer control for task cards
 *
 * Features:
 * - Start/stop timer directly from task card
 * - Shows elapsed time while running
 * - Saves time entry on stop
 * - Visual indicator when timer is active
 */
const TaskTimerButton = ({ task, onTimerChange, compact = false }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [description, setDescription] = useState('');

  // Check if timer is already running for this task (from localStorage)
  useEffect(() => {
    const savedTimer = localStorage.getItem('activeTimer');
    if (savedTimer) {
      const timerData = JSON.parse(savedTimer);
      if (timerData.taskId === task.id) {
        setIsRunning(true);
        setStartTime(new Date(timerData.startTime));
        setDescription(timerData.description || '');
      }
    }
  }, [task.id]);

  // Update elapsed time every second
  useEffect(() => {
    let interval;
    if (isRunning && startTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((new Date() - new Date(startTime)) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, startTime]);

  const handleStartTimer = () => {
    const now = new Date();
    setIsRunning(true);
    setStartTime(now);
    setElapsedSeconds(0);

    // Save to localStorage to persist across page refreshes
    localStorage.setItem('activeTimer', JSON.stringify({
      taskId: task.id,
      taskName: task.name,
      startTime: now.toISOString(),
      description: description,
    }));

    if (onTimerChange) {
      onTimerChange({ running: true, taskId: task.id });
    }
  };

  const handleStopTimer = async () => {
    if (!startTime) return;

    const endTime = new Date();
    const durationHours = (endTime - new Date(startTime)) / (1000 * 60 * 60); // Convert to hours

    try {
      // Create time entry
      await timeEntriesAPI.create({
        task_id: task.id,
        date: localISODate(),
        hours: parseFloat(durationHours.toFixed(2)),
        subtask_comment: description || `Worked on: ${task.name}`,
      });

      // Clear timer state
      setIsRunning(false);
      setStartTime(null);
      setElapsedSeconds(0);
      setDescription('');
      localStorage.removeItem('activeTimer');

      if (onTimerChange) {
        onTimerChange({ running: false, taskId: null });
      }

      // Close menu if open
      handleMenuClose();
    } catch (error) {
      console.error('Error saving time entry:', error);
      alert('Failed to save time entry. Please try again.');
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  // Compact mode - just the button
  if (compact) {
    return (
      <Tooltip title={isRunning ? `Running: ${formatElapsedTime(elapsedSeconds)}` : 'Start timer'}>
        <IconButton
          size="small"
          onClick={isRunning ? handleStopTimer : handleStartTimer}
          sx={{
            bgcolor: isRunning ? 'success.main' : 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: isRunning ? 'success.dark' : 'primary.dark',
            },
            animation: isRunning ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 },
            },
          }}
        >
          {isRunning ? <StopIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    );
  }

  // Full mode with elapsed time display
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isRunning && (
        <Chip
          icon={<TimerIcon />}
          label={formatElapsedTime(elapsedSeconds)}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.8 },
            },
          }}
        />
      )}

      <Tooltip title={isRunning ? 'Stop timer & log time' : 'Start timer'}>
        <IconButton
          size="small"
          onClick={isRunning ? handleMenuOpen : handleStartTimer}
          sx={{
            bgcolor: isRunning ? 'error.main' : 'success.main',
            color: 'white',
            '&:hover': {
              bgcolor: isRunning ? 'error.dark' : 'success.dark',
            },
          }}
        >
          {isRunning ? <StopIcon /> : <PlayIcon />}
        </IconButton>
      </Tooltip>

      {/* Stop Timer Menu - for adding description */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { width: 320, p: 2 },
        }}
      >
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Stop Timer
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Time tracked: {formatElapsedTime(elapsedSeconds)}
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            placeholder="What did you work on?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2, mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleMenuClose}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleStopTimer}
              startIcon={<StopIcon />}
            >
              Stop & Save
            </Button>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default TaskTimerButton;
